import React, { useContext } from "react";
import { InlineMath } from "react-katex";
import { ThemeContext } from "../../../../context/ThemeContext";
import {
  makeSimpleClosedTask,
  normalizeMay2026Variant,
  pick,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const VARIANTS = [
  { opposite: 3, adjacent: 4, hypotenuse: 5 },
  { opposite: 5, adjacent: 12, hypotenuse: 13 },
  { opposite: 8, adjacent: 15, hypotenuse: 17 },
  { opposite: 7, adjacent: 24, hypotenuse: 25 },
  { opposite: 12, adjacent: 5, hypotenuse: 13 },
  { opposite: 9, adjacent: 12, hypotenuse: 15 },
  { opposite: 4, adjacent: 3, hypotenuse: 5 },
  { opposite: 15, adjacent: 8, hypotenuse: 17 },
];

const RADICAL_VARIANTS = [
  { opposite: 4, adjacent: 2, radicalFactor: 2, radicand: 5 },
  { opposite: 6, adjacent: 3, radicalFactor: 3, radicand: 5 },
  { opposite: 8, adjacent: 2, radicalFactor: 2, radicand: 17 },
  { opposite: 9, adjacent: 3, radicalFactor: 3, radicand: 10 },
  { opposite: 12, adjacent: 4, radicalFactor: 4, radicand: 10 },
  { opposite: 12, adjacent: 6, radicalFactor: 6, radicand: 5 },
  { opposite: 15, adjacent: 5, radicalFactor: 5, radicand: 10 },
];

const ORIGINAL_OPTIONS = [
  ["A", "\\frac{1}{\\sqrt{10}}"],
  ["B", "\\frac{1}{3}"],
  ["C", "\\frac{3}{\\sqrt{10}}"],
  ["D", "\\frac{\\sqrt{10}}{\\sqrt{11}}"],
];

const ANGLE_LABELS = [
  { latex: "\\alpha", symbol: "α", name: "alfa" },
  { latex: "\\beta", symbol: "β", name: "beta" },
  { latex: "\\gamma", symbol: "γ", name: "gamma" },
];

const TRIG_SCENARIOS = [
  { angleVertex: "C", functionLatex: "\\sin", numerator: "base", denominator: "hypotenuse" },
  { angleVertex: "C", functionLatex: "\\operatorname{tg}", numerator: "base", denominator: "vertical" },
  { angleVertex: "A", functionLatex: "\\cos", numerator: "base", denominator: "hypotenuse" },
  { angleVertex: "A", functionLatex: "\\operatorname{tg}", numerator: "vertical", denominator: "base" },
];

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function fractionLatex(numerator, denominator) {
  const divisor = gcd(numerator, denominator);
  const n = numerator / divisor;
  const d = denominator / divisor;
  return d === 1 ? String(n) : `\\frac{${n}}{${d}}`;
}

function sideValue(triangle, side) {
  if (side === "base") return triangle.opposite;
  if (side === "vertical") return triangle.adjacent;
  return triangle.hypotenuse;
}

function sideLatex(triangle, side) {
  if (side === "base") return String(triangle.opposite);
  if (side === "vertical") return String(triangle.adjacent);
  return triangle.hypotenuseLatex;
}

function ratioLatex(triangle, numeratorSide, denominatorSide) {
  const numerator = sideValue(triangle, numeratorSide);

  if (denominatorSide === "hypotenuse" && triangle.radicalFactor) {
    const divisor = gcd(numerator, triangle.radicalFactor);
    const reducedNumerator = numerator / divisor;
    const reducedFactor = triangle.radicalFactor / divisor;
    const denominator = reducedFactor === 1
      ? `\\sqrt{${triangle.radicand}}`
      : `${reducedFactor}\\sqrt{${triangle.radicand}}`;
    return `\\frac{${reducedNumerator}}{${denominator}}`;
  }

  return fractionLatex(
    numerator,
    sideValue(triangle, denominatorSide)
  );
}

function buildWrongAnswers(triangle, correct) {
  const candidates = [
    ratioLatex(triangle, "base", "hypotenuse"),
    ratioLatex(triangle, "vertical", "hypotenuse"),
    ratioLatex(triangle, "base", "vertical"),
    ratioLatex(triangle, "vertical", "base"),
  ];

  return [...new Set(candidates)].filter((answer) => answer !== correct).slice(0, 3);
}

function angleNotation(angleVertex) {
  return angleVertex === "A" ? "BAC" : "BCA";
}

function normalizeTriangleData(data = {}) {
  const opposite = Number(data.opposite ?? data.leg);
  const hypotenuseLatex = String(data.hypotenuseLatex ?? data.hyp ?? "");
  let hypotenuse = Number(data.hypotenuse);
  let adjacent = Number(data.adjacent);

  if (!Number.isFinite(hypotenuse) && /^\d+$/.test(hypotenuseLatex)) {
    hypotenuse = Number(hypotenuseLatex);
  }

  // The pre-hot-reload model stored only leg/hyp/sin.
  if (!Number.isFinite(adjacent) && opposite === 6 && hypotenuseLatex === "2\\sqrt{10}") {
    adjacent = 2;
    hypotenuse = 2 * Math.sqrt(10);
  } else if (!Number.isFinite(adjacent) && Number.isFinite(opposite) && Number.isFinite(hypotenuse)) {
    adjacent = Math.sqrt(Math.max(hypotenuse ** 2 - opposite ** 2, 0));
  }

  return {
    ...data,
    opposite: Number.isFinite(opposite) ? opposite : 6,
    adjacent: Number.isFinite(adjacent) ? adjacent : 2,
    hypotenuse: Number.isFinite(hypotenuse) ? hypotenuse : 2 * Math.sqrt(10),
    hypotenuseLatex: hypotenuseLatex || "2\\sqrt{10}",
    sineLatex: data.sineLatex ?? data.sin ?? "\\frac{3}{\\sqrt{10}}",
    angleLatex: data.angleLatex ?? "\\gamma",
    angleSymbol: data.angleSymbol ?? "γ",
    angleName: data.angleName ?? "gamma",
    angleVertex: data.angleVertex ?? "C",
    angleNotation: data.angleNotation ?? angleNotation(data.angleVertex ?? "C"),
    functionLatex: data.functionLatex ?? "\\sin",
  };
}

function RightTriangleSineDiagram({ data }) {
  const { theme = "dark" } = useContext(ThemeContext) || {};
  const textColor = theme === "light" ? "#000" : "#fff";
  const triangleFill = theme === "light" ? "#f9f9f9" : "#000";
  const scale = Math.min(280 / data.opposite, 165 / data.adjacent);
  const drawingWidth = data.opposite * scale;
  const drawingHeight = data.adjacent * scale;
  const ax = (365 - drawingWidth) / 2;
  const ay = 215;
  const bx = ax + drawingWidth;
  const by = ay;
  const cx = bx;
  const cy = ay - drawingHeight;
  const diagonal = Math.hypot(drawingWidth, drawingHeight);
  const angleRadius = 24;
  const angleLabelRadius = 42;
  const hypotenuseLabelX =
    (ax + cx) / 2 - (16 * drawingHeight) / diagonal;
  const hypotenuseLabelY =
    (ay + cy) / 2 - (16 * drawingWidth) / diagonal;

  let anglePath;
  let angleLabelX;
  let angleLabelY;

  if (data.angleVertex === "A") {
    const diagonalX = drawingWidth / diagonal;
    const diagonalY = -drawingHeight / diagonal;
    const bisectorLength = Math.hypot(1 + diagonalX, diagonalY);
    anglePath = `M ${ax + angleRadius} ${ay} A ${angleRadius} ${angleRadius} 0 0 0 ${ax + angleRadius * diagonalX} ${ay + angleRadius * diagonalY}`;
    angleLabelX = ax + (angleLabelRadius * (1 + diagonalX)) / bisectorLength;
    angleLabelY = ay + (angleLabelRadius * diagonalY) / bisectorLength;
  } else {
    const diagonalX = -drawingWidth / diagonal;
    const diagonalY = drawingHeight / diagonal;
    const bisectorLength = Math.hypot(diagonalX, 1 + diagonalY);
    anglePath = `M ${cx} ${cy + angleRadius} A ${angleRadius} ${angleRadius} 0 0 1 ${cx + angleRadius * diagonalX} ${cy + angleRadius * diagonalY}`;
    angleLabelX = cx + (angleLabelRadius * diagonalX) / bisectorLength;
    angleLabelY = cy + (angleLabelRadius * (1 + diagonalY)) / bisectorLength;
  }

  return (
    <svg
      viewBox="0 0 390 250"
      role="img"
      aria-label={`Trójkąt prostokątny ABC z kątem ${data.angleName} przy wierzchołku ${data.angleVertex}`}
      style={{ display: "block", width: "min(100%, 390px)", margin: "8px auto" }}
    >
      <path
        d={`M ${ax} ${ay} L ${bx} ${by} L ${cx} ${cy} Z`}
        fill={triangleFill}
        stroke={textColor}
        strokeWidth="2.2"
      />
      <path
        d={anglePath}
        fill="none"
        stroke="#ff9800"
        strokeWidth="2"
      />
      <path
        d={`M ${bx - 17} ${by} A 17 17 0 0 1 ${bx} ${by - 17}`}
        fill="none"
        stroke={textColor}
        strokeWidth="1.8"
      />
      <circle cx={bx - 6.5} cy={by - 6.5} r="2.3" fill={textColor} />

      <g fill={textColor} fontFamily="KaTeX_Main, Cambria Math, serif" fontSize="19" fontStyle="italic">
        <text x={ax - 17} y={ay + 24}>A</text>
        <text x={bx + 8} y={by + 24}>B</text>
        <text x={cx + 8} y={cy - 5}>C</text>
      </g>
      <text
        x={angleLabelX}
        y={angleLabelY}
        fill="#ff9800"
        fontFamily="KaTeX_Main, Cambria Math, serif"
        fontSize="19"
        fontStyle="italic"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {data.angleSymbol}
      </text>

      <foreignObject
        x={hypotenuseLabelX - 50}
        y={hypotenuseLabelY - 17}
        width="100"
        height="34"
      >
        <div style={{ color: textColor, textAlign: "center", fontSize: 16 }}>
          <InlineMath math={data.hypotenuseLatex} />
        </div>
      </foreignObject>
      <foreignObject x={bx + 10} y={(by + cy) / 2 - 16} width="44" height="32">
        <div style={{ color: textColor, fontSize: 16, textAlign: "left" }}>
          <InlineMath math={String(data.adjacent)} />
        </div>
      </foreignObject>
    </svg>
  );
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const angle = variant === "original" ? ANGLE_LABELS[2] : pick(ANGLE_LABELS);
  const scenario = variant === "original" ? TRIG_SCENARIOS[0] : pick(TRIG_SCENARIOS);
  const triangle = variant === "original"
    ? {
        opposite: 6,
        adjacent: 2,
        hypotenuse: 2 * Math.sqrt(10),
        hypotenuseLatex: "2\\sqrt{10}",
        radicalFactor: 2,
        radicand: 10,
        variantKind: "radical",
        sineLatex: "\\frac{3}{\\sqrt{10}}",
      }
    : (() => {
        const variantKind = pick(["integer", "radical"]);
        if (variantKind === "radical") {
          const selected = pick(RADICAL_VARIANTS);
          const generated = {
            ...selected,
            hypotenuse: selected.radicalFactor * Math.sqrt(selected.radicand),
            hypotenuseLatex: `${selected.radicalFactor}\\sqrt{${selected.radicand}}`,
            variantKind,
          };
          return {
            ...generated,
            sineLatex: ratioLatex(generated, "base", "hypotenuse"),
          };
        }

        const selected = pick(VARIANTS);
        const generated = {
          ...selected,
          hypotenuseLatex: String(selected.hypotenuse),
          variantKind,
        };
        return {
          ...generated,
          sineLatex: ratioLatex(generated, "base", "hypotenuse"),
        };
      })();

  const resultLatex = variant === "original"
    ? triangle.sineLatex
    : ratioLatex(triangle, scenario.numerator, scenario.denominator);
  const wrong = variant === "original"
    ? []
    : buildWrongAnswers(triangle, resultLatex);

  const data = {
    ...triangle,
    // Keep persisted tasks compatible with both the old and current model.
    leg: triangle.opposite,
    hyp: triangle.hypotenuseLatex,
    sin: triangle.sineLatex,
    angleLatex: angle.latex,
    angleSymbol: angle.symbol,
    angleName: angle.name,
    angleVertex: scenario.angleVertex,
    angleNotation: angleNotation(scenario.angleVertex),
    functionLatex: scenario.functionLatex,
    resultLatex,
  };

  return makeSimpleClosedTask({
    type: "MaturaRightTriangleSineClosedTask",
    taskNumber: "18",
    variant,
    prompt: null,
    correct: resultLatex,
    wrong,
    fixedOptions: variant === "original" ? ORIGINAL_OPTIONS : null,
    tags: ["trygonometria", "trojkat prostokatny"],
    data,
    explanationSteps: [
      {
        title: "Najpierw obliczamy długość brakującej przyprostokątnej z twierdzenia Pitagorasa.",
        latex: `|AB|=\\sqrt{|AC|^2-|BC|^2}=\\sqrt{(${triangle.hypotenuseLatex})^2-${triangle.adjacent}^2}=${triangle.opposite}`,
      },
      {
        title: "Korzystamy z definicji odpowiedniej funkcji trygonometrycznej kąta ostrego.",
        latex: `${scenario.functionLatex}${angle.latex}=\\frac{${sideLatex(triangle, scenario.numerator)}}{${sideLatex(triangle, scenario.denominator)}}=${resultLatex}`,
      },
    ],
  });
}

export function renderTask({ data }) {
  const triangle = normalizeTriangleData(data);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <p>
        Dany jest trójkąt prostokątny <InlineMath math="ABC" />, w którym bok <InlineMath math="AC" /> jest{" "}
        przeciwprostokątną oraz <InlineMath math={`|BC|=${triangle.adjacent}`} /> i <InlineMath math={`|AC|=${triangle.hypotenuseLatex}`} />.{" "}
        Kąt <InlineMath math={triangle.angleNotation} /> oznaczamy przez{" "}
        <InlineMath math={triangle.angleLatex} /> (zobacz rysunek).
      </p>
      <RightTriangleSineDiagram data={triangle} />
      <p>{data.instruction}</p>
      <p>
        Wartość wyrażenia{" "}
        <InlineMath math={`${triangle.functionLatex}${triangle.angleLatex}`} /> jest równa:
      </p>
      <div style={{ display: "grid", gap: 8 }}>
        {data.options.map((option) => (
          <div key={option.label}><InlineMath math={`${option.label}.\\;${option.latex}`} /></div>
        ))}
      </div>
    </div>
  );
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedExplanation as renderExplanation,
  renderClosedInput as renderInput,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
